import { EmberTutorialPrompt } from "game-common/models"
import {
    BLEND_MODES,
    Container,
    Graphics,
    InteractionEvent,
    LINE_JOIN,
    Rectangle,
    Sprite,
    Text,
    Texture,
} from "pixi.js"
import { TextRenderer } from "../text/text_renderer"
import { chamfer, FACE, INK, RIM, RIM_LIGHT, TIMBER_EDGE } from "./build_board"
import { createButton } from "./button_factory"
import {
    diamond,
    glowDisc,
    REWARD_EMBER,
    REWARD_GOLD,
    REWARD_GOLD_HOT,
    REWARD_WHITE,
    shineBand,
    sparkBurst,
} from "./chapter_reward_art"
import {
    announceLaterPose,
    announceLaunchPose,
    announcePose,
    ANNOUNCE_IGNITE_AT,
    ANNOUNCE_READY_MS,
    Eased,
    easeOutCubic,
    lerp,
    lerpColor,
} from "./chapter_reward_motion"
import { UiButton } from "./ui_button"
import { HUD_PANEL_TEXT_MUTED } from "./ui_tokens"

// The chapter's title card: a full-viewport cover the server holds the player behind until Begin.
//
// It used to be a flat dark rectangle whose words faded in together. Now it is a beat of its own,
// in the chapter checklist's language (its diamond, its gold, its light-up button): a hearth glow
// comes up from below the frame with embers drifting through it, the chapter's crest ignites, a
// rule draws itself out from it and the words arrive one at a time -- the title rising in ember gold
// and cooling to cream. Then everything but the embers is STILL: the card is an invitation, and
// Begin must not nag. Pressing it is the payoff -- the crest flares, the embers gust upward and the
// card pushes in toward the player as it lifts off the world.
//
// Poses are pure functions of the card's clock (chapter_reward_motion.ts); animate() advances it
// from GuiManager.updateTimer. No filters: the soft glows are one baked radial-gradient texture on
// tinted ADD sprites (stacked fills band visibly at this size), the sparks and rings are Graphics.

export type AnnouncementCue = "reveal" | "launch"

interface Ember {
    x: number
    y: number
    vy: number
    sway: number
    swayMs: number
    phase: number
    age: number
    life: number
    r: number
}

const CREST_R = 24
const CREST_Y = CREST_R + 8
const CREST_INSET = 6
const RULE_GAP = 10
const OVERLINE_GAP = 16
const TITLE_GAP = 8
const BODY_GAP = 14
const ACTIONS_GAP = 34
const BUTTON_H = 36
const BEGIN_W = 176
const LATER_W = 150
const STACK_GAP = 10
// The unlit plate the light crosses as Begin lights up (the checklist's call to action's colours).
const UNLIT_FILL = 0x2a201a
const UNLIT_RIM = 0x6b4f38
const COVER = 0x0d0a08
const TITLE_CREAM = 0xfff3e0
const FLASH = 0xffe2a8
const EMBER_MAX = 46
const EMBER_EVERY_MS = 95
// One frame never advances the card more than this, so a loading hitch under the cover cannot
// swallow the reveal.
const MAX_STEP_MS = 50

const NUMBER_WORDS = [
    "",
    "One",
    "Two",
    "Three",
    "Four",
    "Five",
    "Six",
    "Seven",
    "Eight",
    "Nine",
    "Ten",
    "Eleven",
    "Twelve",
]

/** "CHAPTER TWO" -- the overline spells the number the crest shows. */
export const chapterOverline = (chapter: number) =>
    `CHAPTER ${(NUMBER_WORDS[chapter] || String(chapter)).toUpperCase()}`

let softGlow: Texture | undefined | null
/**
 * A white radial falloff baked once, tinted per use. Null where there is no 2D canvas (the jest
 * mock): the glows are then simply not drawn rather than drawn as solid squares.
 */
const softGlowTexture = (): Texture | null => {
    if (softGlow !== undefined) return softGlow
    try {
        const canvas = document.createElement("canvas")
        canvas.width = canvas.height = 128
        const ctx = canvas.getContext("2d")
        if (!ctx || typeof ctx.createRadialGradient !== "function") return (softGlow = null)
        const gradient = ctx.createRadialGradient(64, 64, 0, 64, 64, 64)
        // Roughly gaussian, so neither the middle nor the rim reads as an edge.
        for (const [stop, alpha] of [
            [0, 1],
            [0.2, 0.72],
            [0.4, 0.38],
            [0.6, 0.15],
            [0.8, 0.04],
            [1, 0],
        ]) {
            gradient.addColorStop(stop, `rgba(255,255,255,${alpha})`)
        }
        ctx.fillStyle = gradient
        ctx.fillRect(0, 0, 128, 128)
        softGlow = Texture.from(canvas)
    } catch (e) {
        softGlow = null
    }
    return softGlow
}

class Glow extends Sprite {
    constructor(color: number) {
        const texture = softGlowTexture()
        super(texture || Texture.EMPTY)
        this.anchor.set(0.5)
        this.tint = color
        this.blendMode = BLEND_MODES.ADD
        this.renderable = !!texture
    }

    /** Centre (x, y), radii (rx, ry) to where the falloff reaches nothing. */
    place = (x: number, y: number, rx: number, ry: number, alpha: number) => {
        this.position.set(x, y)
        this.width = rx * 2
        this.height = ry * 2
        this.alpha = Math.max(0, Math.min(1, alpha))
        this.visible = this.alpha > 0.003
    }
}

export class ChapterAnnouncement extends Container {
    private backdrop = new Graphics()
    private hearthLayer = new Container()
    private hearthWide = new Glow(REWARD_EMBER)
    private hearthCore = new Glow(REWARD_GOLD_HOT)
    private hearthHigh = new Glow(REWARD_GOLD)
    private emberHalos = new Container()
    private halos: Glow[] = []
    private embers = new Graphics()
    private content = new Container()
    private glows = new Container()
    private titleGlow = new Glow(REWARD_GOLD)
    private crestGlow = new Glow(REWARD_GOLD)
    private crestCore = new Glow(REWARD_GOLD_HOT)
    private flash = new Glow(FLASH)
    private nibs = [new Glow(REWARD_GOLD), new Glow(REWARD_GOLD)]
    private under = new Graphics()
    private ornament = new Graphics()
    private words = new Container()
    private actions = new Container()
    private ctaCover = new Graphics()
    private fx = new Graphics()
    private digit?: Text
    private overline?: Text
    private title?: Text
    private description?: Text
    private chapter?: number
    private button?: UiButton
    private laterButton?: UiButton
    private exit?: { kind: "begin" | "later"; ms: number }
    private ms = 0
    private hover = new Eased(0, 160)
    private motes: Ember[] = []
    private moteClock = 0
    private bounds = new Rectangle(0, 0, 800, 600)
    private laidOut?: string
    private layoutHeight = 0
    private ruleHalf = 200
    private fit = 1
    private cueRevealed = false

    constructor(
        private onBegin: (chapter: number) => void,
        private canBegin: () => boolean = () => true,
        private onLater?: (chapter: number) => void,
        private onCue: (cue: AnnouncementCue) => void = () => undefined,
    ) {
        super()
        this.embers.blendMode = BLEND_MODES.ADD
        this.under.blendMode = BLEND_MODES.ADD
        this.fx.blendMode = BLEND_MODES.ADD
        this.hearthLayer.addChild(this.hearthWide, this.hearthCore, this.hearthHigh)
        this.glows.addChild(this.titleGlow, this.crestGlow, this.crestCore, this.flash, ...this.nibs)
        this.content.addChild(this.glows, this.under, this.ornament, this.words, this.actions, this.fx)
        this.addChild(this.backdrop, this.hearthLayer, this.emberHalos, this.embers, this.content)
        this.interactive = true
        // A click on the card while it is still arriving skips straight to the finished card. The
        // buttons take no input until then, so the same press cannot also answer them.
        this.on("pointerdown", (event: InteractionEvent) => {
            event?.stopPropagation()
            if (this.visible && !this.exit && this.ms < ANNOUNCE_READY_MS) this.ms = ANNOUNCE_READY_MS
        })
        this.on("pointermove", (event: any) => event.stopPropagation())
        this.on("rightclick", (event: any) => event.stopPropagation())
        this.visible = false
    }

    open = (prompt: EmberTutorialPrompt) => {
        // Login and camp entry can both announce the same chapter. Preserve its current state.
        if (this.visible && this.chapter === prompt.announcementChapter) return
        this.clearContent()
        this.chapter = prompt.announcementChapter
        this.exit = undefined
        this.ms = 0
        this.moteClock = 0
        this.motes = []
        this.cueRevealed = false
        this.hover.snap(0)
        // The cover is opaque on the very first frame, before room loading can reveal the world.
        this.alpha = 1
        this.visible = true
        const chapter = this.chapter || 1
        this.digit = new TextRenderer()
            .fontSize(22)
            .bold(true)
            .color(0xffffff)
            .strokeThickness(0)
            .render(String(chapter)) as Text
        this.digit.anchor.set(0.5, 0.5)
        this.overline = new TextRenderer()
            .fontSize(13)
            .bold(true)
            .color(RIM_LIGHT)
            .strokeThickness(0)
            .render(chapterOverline(chapter)) as Text
        this.overline.style.letterSpacing = 5
        // White, so the heat can tint it: ember gold on arrival, cooling to the panel cream.
        this.title = new TextRenderer()
            .role("display")
            .color(0xffffff)
            .strokeThickness(0)
            .wordWrap(560)
            .render(prompt.title) as Text
        this.description = new TextRenderer()
            .role("body")
            .color(0xc9bba7)
            .strokeThickness(0)
            .wordWrap(560)
            .render(prompt.body) as Text
        for (const text of [this.overline, this.title, this.description]) {
            text.anchor.set(0.5, 0)
            text.style.align = "center"
        }
        this.words.addChild(this.digit, this.overline, this.title, this.description)
        this.button = createButton({
            label: "Begin",
            theme: "ember",
            shape: "chamfer",
            preset: "primary",
            accent: REWARD_GOLD,
            textColor: INK,
            bold: true,
            width: BEGIN_W,
            height: BUTTON_H,
            size: "medium",
            onClick: this.begin,
        })
        for (const event of ["pointerover", "mouseover"]) this.button.on(event, () => this.hover.set(1, this.ms))
        for (const event of ["pointerout", "mouseout", "pointerupoutside", "pointercancel"]) {
            this.button.on(event, () => this.hover.set(0, this.ms))
        }
        this.actions.addChild(this.button)
        if (chapter >= 2) {
            this.laterButton = createButton({
                label: "Later",
                theme: "ember",
                shape: "chamfer",
                variant: "secondary",
                textColor: HUD_PANEL_TEXT_MUTED,
                width: LATER_W,
                height: BUTTON_H,
                size: "medium",
                onClick: this.maybeLater,
            })
            this.actions.addChild(this.laterButton)
        }
        this.actions.addChild(this.ctaCover)
        this.actions.interactiveChildren = false
        this.laidOut = undefined
        this.resize(this.bounds.width, this.bounds.height, this.bounds.x, this.bounds.y)
        this.pose()
    }

    private clearContent = () => {
        for (const child of [this.digit, this.overline, this.title, this.description, this.button, this.laterButton]) {
            if (child && !child.destroyed) child.destroy({ children: true })
        }
        this.digit = this.overline = this.title = this.description = undefined
        this.button = this.laterButton = undefined
        this.ctaCover.clear()
    }

    /** Called every frame with the screen in local coordinates; lays out only when something changed. */
    resize = (width: number, height: number, x = 0, y = 0) => {
        this.bounds = new Rectangle(x, y, width, height)
        this.hitArea = this.bounds
        const key = `${x}|${y}|${width}|${height}|${this.chapter}`
        if (this.laidOut === key) return
        this.laidOut = key
        this.backdrop.clear().beginFill(COVER, 1).drawRect(x, y, width, height).endFill()
        // The hearth: a warm glow from a fire just below the frame, so the card reads as a story
        // told at the fire rather than a loading screen.
        const cx = x + width / 2
        this.hearthWide.place(cx, y + height * 1.12, width * 0.78, height * 0.9, 0.62)
        this.hearthCore.place(cx, y + height * 1.04, width * 0.36, height * 0.42, 0.42)
        this.hearthHigh.place(cx, y + height * 0.4, width * 0.42, height * 0.36, 0.08)
        this.layoutContent(width, height, x, y)
    }

    private layoutContent = (width: number, height: number, x: number, y: number) => {
        const { overline, title, description, button } = this
        if (!overline || !title || !description || !button || !this.digit) return
        const wrap = Math.min(560, Math.max(160, width - 48))
        title.style.wordWrapWidth = wrap
        description.style.wordWrapWidth = wrap
        const overlineY = CREST_Y + CREST_R + OVERLINE_GAP
        const titleY = overlineY + overline.height + TITLE_GAP
        const bodyY = titleY + Math.max(title.height, 48) + BODY_GAP
        const actionsY = bodyY + Math.max(description.height, 22) + ACTIONS_GAP
        overline.position.set(0, overlineY)
        title.position.set(0, titleY)
        description.position.set(0, bodyY)
        this.digit.position.set(0, CREST_Y)
        const stacked = width < 400
        this.actions.position.set(0, actionsY)
        if (this.laterButton && !stacked) {
            button.position.set(6, 0)
            this.laterButton.position.set(-6 - LATER_W, 0)
        } else {
            button.position.set(-BEGIN_W / 2, 0)
            this.laterButton?.position.set(-LATER_W / 2, BUTTON_H + STACK_GAP)
        }
        this.layoutHeight = actionsY + BUTTON_H + (this.laterButton && stacked ? BUTTON_H + STACK_GAP : 0)
        this.ruleHalf = Math.max(CREST_R + RULE_GAP + 40, Math.min(230, width / 2 - 36))
        this.fit = Math.min(1, Math.max(0.2, (height - 48) / Math.max(1, this.layoutHeight)))
        // Pivot on the middle so the push-in on Begin grows from the centre of the words.
        this.content.pivot.set(0, this.layoutHeight / 2)
        this.content.x = x + width / 2
        // A touch above centre: the optical middle of a title card.
        const half = (this.layoutHeight * this.fit) / 2
        this.content.y = Math.min(y + Math.max(24 + half, height * 0.47), y + height - 24 - half)
        this.content.scale.set(this.fit)
    }

    isOpen = (): boolean => this.visible

    /** True once the card has finished arriving and its actions take input. */
    isReady = (): boolean => this.visible && !this.exit && this.ms >= ANNOUNCE_READY_MS

    /** Begin is deliberately unavailable until the card has finished arriving. */
    begin = () => {
        if (!this.isReady() || !this.canBegin()) return
        this.exit = { kind: "begin", ms: 0 }
        this.actions.interactiveChildren = false
        this.onCue("launch")
    }

    maybeLater = () => {
        if (!this.isReady() || (this.chapter || 1) < 2 || !this.canBegin()) return
        this.exit = { kind: "later", ms: 0 }
        this.actions.interactiveChildren = false
    }

    close = () => {
        this.chapter = undefined
        this.visible = false
        this.exit = undefined
        this.motes = []
        this.embers.clear()
        this.halos.forEach(halo => {
            halo.visible = false
        })
    }

    animate = (dtMs: number) => {
        if (!this.visible) return
        const dt = Math.max(0, Math.min(MAX_STEP_MS, dtMs || 0))
        this.ms += dt
        if (this.exit) this.exit.ms += dt
        if (!this.cueRevealed && this.ms >= ANNOUNCE_IGNITE_AT) {
            this.cueRevealed = true
            this.onCue("reveal")
        }
        this.stepEmbers(dt)
        this.pose()
        const exit = this.exit
        if (exit && (exit.kind === "begin" ? announceLaunchPose(exit.ms) : announceLaterPose(exit.ms)).done) {
            const chapter = this.chapter
            this.close()
            if (chapter !== undefined) {
                if (exit.kind === "later") this.onLater?.(chapter)
                else this.onBegin(chapter)
            }
        }
    }

    // ---- Posing -------------------------------------------------------------------------------

    private pose = () => {
        const pose = announcePose(this.ms)
        const launch = this.exit?.kind === "begin" ? announceLaunchPose(this.exit.ms) : undefined
        const later = this.exit?.kind === "later" ? announceLaterPose(this.exit.ms) : undefined
        const flare = launch?.flare || 0
        this.alpha = launch ? launch.coverAlpha : later ? later.coverAlpha : 1
        this.content.alpha = launch ? launch.contentAlpha : later ? later.contentAlpha : 1
        this.content.scale.set(this.fit * (1 + 0.08 * (launch?.push || 0)))
        this.hearthLayer.alpha = pose.hearth * (0.8 + 0.2 * flare)
        if (!this.actions.interactiveChildren && pose.ready && !this.exit) this.actions.interactiveChildren = true

        const fx = this.fx.clear()
        this.under.clear()
        this.poseCrest(pose, flare)
        this.poseRule(pose)
        const lit = pose.lit * pose.crestAlpha
        this.flash.place(0, CREST_Y, 130, 130, 0.55 * pose.flash)
        this.crestGlow.place(0, CREST_Y, 90 + 50 * flare, 90 + 50 * flare, 0.42 * lit + 0.4 * flare)
        this.crestCore.place(0, CREST_Y, 42, 42, 0.5 * lit + 0.3 * flare)
        this.ring(fx, pose.ringT)
        if (launch) this.ring(fx, launch.ringT)
        sparkBurst(fx, 0, CREST_Y, pose.burstT, 16, CREST_R + 2, CREST_R + 66, REWARD_GOLD_HOT, 0.2, 1.8)
        if (launch) sparkBurst(fx, 0, CREST_Y, launch.burstT, 14, CREST_R + 4, CREST_R + 84, REWARD_GOLD, 0.45, 1.6)

        if (this.overline) {
            this.overline.alpha = pose.overlineAlpha
            this.overline.pivot.y = -pose.overlineRise
        }
        if (this.title) {
            const heat = Math.max(pose.heat, 0.65 * flare)
            this.title.alpha = pose.titleAlpha
            this.title.pivot.y = -pose.titleRise / pose.titleScale
            this.title.scale.set(pose.titleScale)
            this.title.tint = lerpColor(TITLE_CREAM, REWARD_GOLD_HOT, heat)
            const w = this.title.width,
                h = this.title.height
            const top = this.title.y + pose.titleRise
            this.titleGlow.place(0, top + h / 2, w * 0.7 + 40, h * 0.9 + 30, pose.titleAlpha * (0.14 + 0.26 * heat))
            if (pose.titleShineT >= 0) shineBand(fx, -w / 2, top, w, h, pose.titleShineT, 70, 0.32)
        }
        if (this.description) {
            this.description.alpha = pose.bodyAlpha * (launch ? launch.bodyAlpha : 1)
            this.description.pivot.y = -pose.bodyRise
        }
        this.poseActions(pose, launch, this.under, fx)
    }

    private poseCrest = (pose: ReturnType<typeof announcePose>, flare: number) => {
        const g = this.ornament.clear()
        const alpha = pose.crestAlpha
        if (!(alpha > 0)) {
            if (this.digit) this.digit.alpha = 0
            return
        }
        const s = pose.crestScale
        const r = CREST_R * s,
            cy = CREST_Y,
            lit = pose.lit
        g.beginFill(0x000000, 0.45 * alpha)
        diamond(g, 0, cy + 3, r)
        g.endFill()
        g.lineStyle({ width: 1.5, color: TIMBER_EDGE, alpha, join: LINE_JOIN.MITER })
        g.beginFill(lerpColor(UNLIT_RIM, REWARD_GOLD_HOT, lit), alpha)
        diamond(g, 0, cy, r)
        g.endFill()
        g.lineStyle(0)
        // The upper facets of the frame catch the light once it is lit.
        if (lit > 0) {
            g.beginFill(REWARD_GOLD, alpha * lit)
            g.moveTo(0, cy - r + 1)
                .lineTo(r - 1, cy)
                .lineTo(0, cy + 0.5)
                .lineTo(-r + 1, cy)
                .closePath()
            g.endFill()
            g.beginFill(REWARD_WHITE, 0.7 * alpha * lit)
            g.moveTo(0, cy - r + 1.5)
                .lineTo(-r * 0.5, cy - r * 0.5 + 0.5)
                .lineTo(0, cy - r * 0.62)
                .closePath()
            g.endFill()
        }
        const inner = r - CREST_INSET * s
        g.lineStyle({ width: 1, color: lerpColor(RIM, REWARD_GOLD, lit), alpha, join: LINE_JOIN.MITER })
        g.beginFill(FACE, alpha)
        diamond(g, 0, cy, inner)
        g.endFill()
        g.lineStyle(0)
        if (this.digit) {
            this.digit.alpha = alpha * (0.35 + 0.65 * lit)
            this.digit.scale.set(s)
            this.digit.tint = lerpColor(
                lerpColor(UNLIT_RIM, REWARD_GOLD, lit),
                REWARD_WHITE,
                0.6 * Math.max(pose.flash, flare),
            )
        }
    }

    /** The rule drawn outward from the crest, a small diamond leading each end like a pen nib. */
    private poseRule = (pose: ReturnType<typeof announcePose>) => {
        this.nibs.forEach(nib => {
            nib.visible = false
        })
        if (!(pose.rule > 0)) return
        const g = this.ornament
        const from = CREST_R + RULE_GAP,
            full = this.ruleHalf - from
        const reach = full * pose.rule
        const SEGMENTS = 14
        ;[-1, 1].forEach((side, index) => {
            for (let i = 0; i < SEGMENTS; i++) {
                const a = (i / SEGMENTS) * full,
                    b = ((i + 1) / SEGMENTS) * full
                if (a >= reach) break
                const k = i / SEGMENTS
                g.lineStyle(1.5, lerpColor(REWARD_GOLD, RIM, k), 0.9 - 0.75 * k)
                g.moveTo(side * (from + a), CREST_Y).lineTo(side * (from + Math.min(b, reach)), CREST_Y)
            }
            g.lineStyle(0)
            const tip = side * (from + reach)
            g.beginFill(lerpColor(REWARD_GOLD_HOT, RIM_LIGHT, pose.rule), 0.95)
            diamond(g, tip, CREST_Y, 3.2)
            g.endFill()
            // The nib glows while it is still moving.
            this.nibs[index].place(tip, CREST_Y, 16, 16, 0.9 * (1 - pose.rule))
        })
    }

    /** A diamond shockwave off the crest. */
    private ring = (fx: Graphics, t: number) => {
        if (!(t > 0) || t >= 1) return
        const k = easeOutCubic(t)
        fx.lineStyle({ width: 2.5 * (1 - t) + 0.5, color: REWARD_GOLD, alpha: 0.9 * (1 - t), join: LINE_JOIN.MITER })
        diamond(fx, 0, CREST_Y, CREST_R + 4 + 60 * k)
        fx.lineStyle(0)
    }

    private poseActions = (
        pose: ReturnType<typeof announcePose>,
        launch: ReturnType<typeof announceLaunchPose> | undefined,
        under: Graphics,
        over: Graphics,
    ) => {
        const button = this.button
        const cover = this.ctaCover.clear()
        if (!button) return
        const cta = pose.actions
        this.actions.alpha = cta.plateAlpha * (launch ? launch.actionsAlpha : 1)
        this.actions.pivot.y = -cta.rise
        const bx = button.x,
            by = this.actions.y + button.y + cta.rise,
            w = BEGIN_W,
            h = BUTTON_H
        // The unlit plate over Begin, drawn back from the left as the light comes.
        if (cta.ignite < 1) {
            const front = w * cta.ignite
            const c = Math.min(5, h / 4)
            cover.lineStyle({ width: 1, color: UNLIT_RIM, alpha: 1, join: LINE_JOIN.MITER })
            cover.beginFill(UNLIT_FILL, 1)
            if (front <= c) chamfer(cover, bx, button.y, w, h, c)
            else {
                const left = bx + front,
                    top = button.y
                cover
                    .moveTo(left, top)
                    .lineTo(bx + w - c, top)
                    .lineTo(bx + w, top + c)
                    .lineTo(bx + w, top + h - c)
                    .lineTo(bx + w - c, top + h)
                    .lineTo(left, top + h)
                    .closePath()
            }
            cover.endFill()
            cover.lineStyle(0)
        }
        const actionsAlpha = this.actions.alpha
        if (!(actionsAlpha > 0)) return
        if (cta.edge > 0) {
            const x = bx + w * cta.ignite
            for (let i = 0; i < 3; i++) {
                over.beginFill(i === 0 ? REWARD_WHITE : REWARD_GOLD, actionsAlpha * cta.edge * (0.55 - i * 0.17))
                    .drawRect(x - 1 - i * 2, by - i * 1.5, 2 + i * 4, h + i * 3)
                    .endFill()
            }
            glowDisc(over, x, by + h / 2, 16, REWARD_GOLD, 0.35 * cta.edge * actionsAlpha, 3)
        }
        const hover = this.hover.value(this.ms)
        const warm = (0.6 * cta.glow + 0.3 * hover + 0.8 * (launch?.flare || 0)) * actionsAlpha
        if (warm > 0.01) {
            for (let i = 0; i < 4; i++) {
                const grow = 2 + i * 3
                under.lineStyle({
                    width: 3 + i * 2,
                    color: i < 2 ? REWARD_GOLD : REWARD_EMBER,
                    alpha: warm * (0.34 - i * 0.07),
                    join: LINE_JOIN.MITER,
                })
                chamfer(under, bx - grow, by - grow, w + grow * 2, h + grow * 2, 6)
            }
            under.lineStyle(0)
        }
        if (cta.shineT >= 0 && !launch)
            shineBand(over, bx + 2, by + 2, w - 4, h - 4, cta.shineT, 30, 0.22 * actionsAlpha)
        if (launch) {
            over.beginFill(REWARD_GOLD, 0.4 * launch.flare * actionsAlpha)
            chamfer(over, bx, by, w, h, 5)
            over.endFill()
        }
    }

    // ---- Embers -------------------------------------------------------------------------------

    private stepEmbers = (dt: number) => {
        const { x, y, width, height } = this.bounds
        const launch = this.exit?.kind === "begin" ? announceLaunchPose(this.exit.ms) : undefined
        const gust = launch?.gust || 0
        // A thicker puff while the fire catches, and a gust when Begin is pressed.
        const every = EMBER_EVERY_MS / (this.ms < 700 ? 2 : 1) / (1 + 2.5 * gust)
        this.moteClock += dt
        while (this.moteClock >= every) {
            this.moteClock -= every
            if (this.motes.length >= EMBER_MAX + 30 * gust) continue
            // Clustered toward the middle, where the fire below the frame would be.
            const spread = (Math.random() + Math.random() + Math.random()) / 3 - 0.5
            this.motes.push({
                x: x + width * (0.5 + spread * 1.5),
                y: y + height + 4,
                vy: -(0.03 + Math.random() * 0.05),
                sway: 4 + Math.random() * 12,
                swayMs: 1400 + Math.random() * 1600,
                phase: Math.random() * Math.PI * 2,
                age: 0,
                life: 2600 + Math.random() * 3400,
                r: 0.7 + Math.random() * 1.4,
            })
        }
        const speed = lerp(1, 5, gust)
        this.motes = this.motes.filter(mote => mote.age < mote.life)
        this.motes.forEach(mote => {
            mote.age += dt
            mote.y += mote.vy * dt * speed
        })
        const g = this.embers.clear()
        const hearth = announcePose(this.ms).hearth
        this.motes.forEach((mote, index) => {
            // Each ember carries a soft halo sprite: a drawn circle reads as a bubble at full size.
            const halo = this.halos[index] || this.emberHalos.addChild((this.halos[index] = new Glow(REWARD_EMBER)))
            const k = mote.age / mote.life
            const fade = Math.min(1, k / 0.15) * Math.min(1, (1 - k) / 0.45)
            const alpha = fade * hearth
            const mx = mote.x + Math.sin(mote.phase + (mote.age / mote.swayMs) * Math.PI * 2) * mote.sway
            halo.place(mx, mote.y, mote.r * 5, mote.r * 5, 0.32 * alpha)
            if (!(alpha > 0.01)) return
            const color = k < 0.25 ? REWARD_GOLD : k < 0.6 ? REWARD_GOLD_HOT : REWARD_EMBER
            g.beginFill(color, 0.9 * alpha)
                .drawCircle(mx, mote.y, mote.r)
                .endFill()
        })
        for (let i = this.motes.length; i < this.halos.length; i++) this.halos[i].visible = false
    }

    destroy = () => {
        this.clearContent()
        super.destroy({ children: true })
    }
}
